import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import QrModal from './QrModal';
import './PeerTable.css';

export default function PeerTable({ peers, onUpdate }) {
  const { getAccessTokenSilently } = useAuth0();
  const [editing, setEditing] = useState({}); // { [peerId]: { pretty_name, assigned_to } }
  const [qrPeer, setQrPeer] = useState(null);

  const startEdit = (peer) => {
    setEditing(prev => ({
      ...prev,
      [peer.id]: { pretty_name: peer.pretty_name, assigned_to: peer.assigned_to },
    }));
  };

  const cancelEdit = (peerId) => {
    setEditing(prev => { const n = { ...prev }; delete n[peerId]; return n; });
  };

  const saveEdit = async (peerId) => {
    await onUpdate(peerId, editing[peerId]);
    cancelEdit(peerId);
  };

  const onChange = (peerId, field, value) => {
    setEditing(prev => ({ ...prev, [peerId]: { ...prev[peerId], [field]: value } }));
  };

  /**
   * Download the .conf file via an authenticated fetch so the Bearer token is
   * included. A plain <a href> would omit the Authorization header, causing 401.
   */
  const handleDownload = async (peer) => {
    const token = await getAccessTokenSilently();
    const res = await fetch(`/api/vpn/peers/${peer.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `peer${peer.id}.conf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {qrPeer && <QrModal peer={qrPeer} onClose={() => setQrPeer(null)} />}
      <div className="table-wrapper">
        <table className="peer-table">
          <thead>
            <tr>
              <th>#</th>
              <th>IP Address</th>
              <th>Pretty Name</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {peers.map(peer => {
              const isEditing = !!editing[peer.id];
              const editData = editing[peer.id] || {};
              return (
                <tr key={peer.id} className={isEditing ? 'editing' : ''}>
                  <td className="peer-id">{peer.id}</td>
                  <td className="peer-ip">{peer.ip}</td>
                  <td>
                    {isEditing ? (
                      <input
                        className="table-input"
                        value={editData.pretty_name}
                        onChange={e => onChange(peer.id, 'pretty_name', e.target.value)}
                        placeholder="Device name…"
                      />
                    ) : (
                      <span className="name-cell">{peer.pretty_name || <span className="empty">—</span>}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="table-input"
                        value={editData.assigned_to}
                        onChange={e => onChange(peer.id, 'assigned_to', e.target.value)}
                        placeholder="user@email.com"
                      />
                    ) : (
                      <span className="email-cell">{peer.assigned_to || <span className="empty">Unassigned</span>}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${peer.online ? 'online' : 'offline'}`}>
                      {peer.online ? '● Online' : '○ Offline'}
                    </span>
                  </td>
                  <td className="actions">
                    {isEditing ? (
                      <>
                        <button className="btn-save" onClick={() => saveEdit(peer.id)}>Save</button>
                        <button className="btn-cancel" onClick={() => cancelEdit(peer.id)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn-edit" onClick={() => startEdit(peer)}>Edit</button>
                        <button className="btn-dl" onClick={() => handleDownload(peer)}>↓ Conf</button>
                        <button className="btn-qr" onClick={() => setQrPeer(peer)}>QR</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
